import { useMutation, useQuery } from "@tanstack/react-query";
import { api, json } from "../../../api";
import type {
  BillingProduct,
  BillingInvoice,
  BillingStatus,
  BillingProfile,
  UpgradeCheckoutInput,
  UpgradeCheckoutOutput,
} from "../types/billing";

type UseBillingOptions = {
  includeInvoices?: boolean;
  includeProducts?: boolean;
};

export function useBilling(
  workspaceId: string | undefined,
  {
    includeInvoices = false,
    includeProducts = true,
  }: UseBillingOptions = {},
) {
  const query = useQuery({
    queryKey: ["billing", workspaceId],
    queryFn: () => api<BillingStatus>(`/${workspaceId}/billing`),
  });
  const products = useQuery({
    queryKey: ["products", "billing", workspaceId],
    enabled: Boolean(workspaceId) && includeProducts,
    queryFn: () => api<{ items: BillingProduct[] }>("/products"),
  });
  const invoices = useQuery({
    queryKey: ["billing", workspaceId, "invoices"],
    enabled:
      includeInvoices &&
      Boolean(workspaceId) &&
      query.data?.role === "owner",
    queryFn: () =>
      api<{ items: BillingInvoice[] }>(`/${workspaceId}/billing/invoices`),
  });
  const upgrade = useMutation({
    mutationFn: (input: UpgradeCheckoutInput) =>
      api<UpgradeCheckoutOutput>(
        `/${workspaceId}/billing/checkout`,
        json(input),
      ),
    onSuccess: async ({ url }) => {
      if (url) {
        window.location.assign(url);
        return;
      }
      await query.refetch();
    },
  });
  const updateProfile = useMutation({
    mutationFn: (input: BillingProfile) =>
      api<BillingProfile>(
        `/${workspaceId}/billing/profile`,
        {
          ...json(input),
          method: "PATCH",
        },
      ),
    onSuccess: async () => {
      await query.refetch();
    },
  });

  async function action(path: string, body?: unknown) {
    const value = await api<{ url?: string }>(
      `/${workspaceId}/billing/${path}`,
      json(body ?? {}),
    );
    if (value?.url) {
      location.href = value.url;
    } else {
      await query.refetch();
    }
  }

  async function openPortal() {
    const portalWindow = window.open("about:blank", "_blank");
    if (!portalWindow) {
      throw new Error("Allow pop-ups to open the Stripe billing portal.");
    }

    portalWindow.opener = null;
    try {
      const value = await api<{ url?: string }>(
        `/${workspaceId}/billing/portal`,
        json({}),
      );
      if (!value.url) {
        throw new Error("Stripe did not return a billing portal URL.");
      }
      portalWindow.location.replace(value.url);
    } catch (error) {
      portalWindow.close();
      throw error;
    }
  }

  return {
    query,
    products,
    invoices,
    upgrade,
    updateProfile,
    action,
    openPortal,
  };
}
